import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  getPendientes,
  getErrores,
  asignarCuenta,
  asignarMasivo,
  aplicarReglaGlobal,
  buscarCuentas,
  getPartidasBalance,
  getPartidasPyg,
  getEjercicio,
  crearCuenta,
  borrarCuenta,
  renombrarCuenta,
} from "@/lib/api";
import { esCuentaDeAsiento } from "@/lib/useModoAsiento";
import { formatCurrency } from "@/lib/utils";
import { notifyDataChanged } from "@/lib/events";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, AlertCircle, CheckCircle2, XCircle, Search, Plus, Trash2, Pencil } from "lucide-react";

// Etiqueta y color del bucket sugerido (activo/pasivo/resultado).
const SUG = {
  activo: { label: "Activo", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" },
  pasivo: { label: "Pasivo", cls: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400" },
  resultado: { label: "Resultado", cls: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400" },
};

export default function RevisionPage() {
  const { ejercicioId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const buscarInicial = searchParams.get("buscar") || "";
  const [pendientes, setPendientes] = useState([]);
  const [errores, setErrores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCuenta, setSelectedCuenta] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [partidasBalance, setPartidasBalance] = useState([]);
  const [partidasPyg, setPartidasPyg] = useState([]);

  // Pestaña Buscar (deep-link: /revision?buscar=XXXX abre la pestaña con la búsqueda hecha)
  const [activeTab, setActiveTab] = useState(buscarInicial ? "buscar" : "pendientes");
  const [busqueda, setBusqueda] = useState(buscarInicial);
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [buscando, setBuscando] = useState(false);
  
  // Form state
  const [tipoDestino, setTipoDestino] = useState("BALANCE");
  const [partidaSeleccionada, setPartidaSeleccionada] = useState("");
  const [guardarEmpresa, setGuardarEmpresa] = useState(false);
  const [guardarGlobal, setGuardarGlobal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Selección múltiple + asignación masiva
  const [seleccionadas, setSeleccionadas] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTipo, setBulkTipo] = useState("BALANCE");
  const [bulkPartida, setBulkPartida] = useState("");
  const [bulkGuardar, setBulkGuardar] = useState(false);
  // Diálogo "¿aplicar también al resto de empresas?"
  const [promptGlobal, setPromptGlobal] = useState(null); // {cuenta3s, tipo_destino, partida_pgc}

  // F1: alta manual de cuenta (se crea con saldo 0)
  const [altaOpen, setAltaOpen] = useState(false);
  const [altaCodigo, setAltaCodigo] = useState("");
  const [altaNombre, setAltaNombre] = useState("");
  const [altaClasificar, setAltaClasificar] = useState(false);
  const [altaTipo, setAltaTipo] = useState("BALANCE");
  const [altaPartida, setAltaPartida] = useState("");
  const [creando, setCreando] = useState(false);

  // F1: borrado de cuenta creada a mano (solo motivo === "Alta manual")
  const [cuentaABorrar, setCuentaABorrar] = useState(null);
  // Renombrar cualquier cuenta desde "el origen" (petición Héctor sept-2026):
  // en las importadas el nombre se propaga a TODOS los ejercicios de la
  // empresa y sobrevive a reimportaciones (ver backend, domain/renombres.py).
  const [cuentaARenombrar, setCuentaARenombrar] = useState(null);
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [renombrando, setRenombrando] = useState(false);
  const [borrando, setBorrando] = useState(false);

  useEffect(() => {
    loadData();
    loadPartidas();
  }, [ejercicioId]);

  // Búsqueda con debounce sobre todas las cuentas del ejercicio
  useEffect(() => {
    const texto = busqueda.trim();
    if (!texto) {
      setResultadosBusqueda([]);
      setBuscando(false);
      return undefined;
    }
    setBuscando(true);
    const timer = setTimeout(async () => {
      try {
        const res = await buscarCuentas(ejercicioId, texto);
        setResultadosBusqueda(res.data);
      } catch (error) {
        toast.error("Error al buscar cuentas");
      } finally {
        setBuscando(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [busqueda, ejercicioId]);

  const handleRenombrar = async () => {
    const nombre = nuevoNombre.trim();
    if (!nombre || !cuentaARenombrar) return;
    setRenombrando(true);
    try {
      const res = await renombrarCuenta(ejercicioId, cuentaARenombrar.id, nombre);
      const n = res.data?.cuentas_actualizadas;
      toast.success(
        n > 1
          ? `Cuenta renombrada en ${n} ejercicios de la empresa`
          : "Cuenta renombrada"
      );
      setCuentaARenombrar(null);
      setNuevoNombre("");
      notifyDataChanged();
      await refrescarBusqueda();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al renombrar la cuenta");
    } finally {
      setRenombrando(false);
    }
  };

  const refrescarBusqueda = async () => {
    const texto = busqueda.trim();
    if (!texto) return;
    try {
      const res = await buscarCuentas(ejercicioId, texto);
      setResultadosBusqueda(res.data);
    } catch {
      // silencioso: la tabla mantiene los resultados anteriores
    }
  };

  const loadData = async () => {
    try {
      const [pendientesRes, erroresRes] = await Promise.all([
        getPendientes(ejercicioId),
        getErrores(ejercicioId),
      ]);
      setPendientes(pendientesRes.data);
      setErrores(erroresRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const loadPartidas = async () => {
    try {
      const [balanceRes, pygRes] = await Promise.all([
        getPartidasBalance(),
        getPartidasPyg(),
      ]);
      setPartidasBalance(balanceRes.data);
      setPartidasPyg(pygRes.data);
    } catch (error) {
      console.error("Error loading partidas");
    }
  };

  const handleSelectCuenta = (cuenta) => {
    setSelectedCuenta(cuenta);
    // Precarga: si ya está clasificada, su destino/partida; si no, la SUGERENCIA.
    const clasif = cuenta.clasificacion_actual;
    setTipoDestino(clasif?.tipo_destino || cuenta.tipo_destino || cuenta.sugerencia?.tipo_destino || "BALANCE");
    setPartidaSeleccionada(clasif?.partida_pgc || cuenta.partida_pgc || "");
    setGuardarEmpresa(false);
    setGuardarGlobal(false);
    setSheetOpen(true);
  };

  // Tras asignar/masivo, refresca todo y (si se guardó regla de empresa, no global)
  // ofrece aplicarla al resto de empresas.
  const trasAsignar = (reclasificadas, cuenta3s, tipo_destino, partida_pgc, esEmpresa, esGlobal) => {
    setSeleccionadas(new Set());
    loadData();
    refrescarBusqueda();
    notifyDataChanged("ejercicios");
    if (reclasificadas > 1) toast.success(`${reclasificadas} cuentas clasificadas con la regla`);
    else toast.success("Cuenta asignada correctamente");
    if (esEmpresa && !esGlobal && cuenta3s.length) {
      setPromptGlobal({ cuenta3s, tipo_destino, partida_pgc });
    }
  };

  const handleAsignar = async () => {
    if (!partidaSeleccionada) {
      toast.error("Selecciona una partida");
      return;
    }
    setSaving(true);
    try {
      const res = await asignarCuenta(ejercicioId, {
        cuenta_id: selectedCuenta.id,
        tipo_destino: tipoDestino,
        partida_pgc: partidaSeleccionada,
        guardar_empresa: guardarEmpresa,
        guardar_global: guardarGlobal,
      });
      setSheetOpen(false);
      const n = (res.data?.reclasificadas || 0) + 1;
      trasAsignar(n, selectedCuenta.cuenta3 ? [selectedCuenta.cuenta3] : [],
        tipoDestino, partidaSeleccionada, guardarEmpresa, guardarGlobal);
    } catch (error) {
      toast.error("Error al asignar cuenta");
    } finally {
      setSaving(false);
    }
  };

  const toggleSel = (id) => {
    setSeleccionadas((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleTodas = (ids, marcar) => {
    setSeleccionadas((prev) => {
      const s = new Set(prev);
      ids.forEach((id) => (marcar ? s.add(id) : s.delete(id)));
      return s;
    });
  };

  const handleAsignarMasivo = async () => {
    if (!bulkPartida) { toast.error("Selecciona una partida"); return; }
    setSaving(true);
    try {
      const ids = [...seleccionadas];
      const cuenta3s = [...new Set(pendientes.filter((c) => seleccionadas.has(c.id)).map((c) => c.cuenta3))];
      const res = await asignarMasivo(ejercicioId, {
        cuenta_ids: ids,
        tipo_destino: bulkTipo,
        partida_pgc: bulkPartida,
        guardar_empresa: bulkGuardar,
        guardar_global: false,
      });
      setBulkOpen(false);
      trasAsignar(res.data?.reclasificadas || ids.length, cuenta3s, bulkTipo, bulkPartida, bulkGuardar, false);
    } catch (error) {
      toast.error("Error al asignar las cuentas");
    } finally {
      setSaving(false);
    }
  };

  const handleAplicarGlobal = async () => {
    if (!promptGlobal) return;
    try {
      for (const c3 of promptGlobal.cuenta3s) {
        await aplicarReglaGlobal(ejercicioId, {
          cuenta3: c3,
          tipo_destino: promptGlobal.tipo_destino,
          partida_pgc: promptGlobal.partida_pgc,
        });
      }
      toast.success("Regla aplicada a todas tus empresas");
      loadData();
      notifyDataChanged("ejercicios");
    } catch {
      toast.error("No se pudo aplicar al resto de empresas");
    } finally {
      setPromptGlobal(null);
    }
  };

  const abrirAlta = () => {
    setAltaCodigo("");
    setAltaNombre("");
    setAltaClasificar(false);
    setAltaTipo("BALANCE");
    setAltaPartida("");
    setAltaOpen(true);
  };

  const handleCrearCuenta = async (e) => {
    e.preventDefault();
    const codigo = altaCodigo.trim();
    const nombre = altaNombre.trim();
    if (!codigo) { toast.error("Introduce el código de la cuenta"); return; }
    if (!nombre) { toast.error("Introduce el nombre de la cuenta"); return; }
    if (altaClasificar && !altaPartida) { toast.error("Selecciona una partida o desactiva la clasificación manual"); return; }
    setCreando(true);
    try {
      const payload = { codigo_full: codigo, nombre };
      // El backend exige tipo_destino Y partida_pgc juntos, o ninguno.
      if (altaClasificar) {
        payload.tipo_destino = altaTipo;
        payload.partida_pgc = altaPartida;
      }
      await crearCuenta(ejercicioId, payload);
      toast.success(`Cuenta ${codigo} creada con saldo 0`);
      setAltaOpen(false);
      loadData();
      refrescarBusqueda();
      notifyDataChanged("ejercicios");
    } catch (error) {
      // 400 (código <3 dígitos / clasificación parcial) y 409 (ya existe) traen detail.
      toast.error(error.response?.data?.detail || "Error al crear la cuenta");
    } finally {
      setCreando(false);
    }
  };

  // Borra una cuenta creada a mano. El backend solo permite borrar las de
  // motivo "Alta manual" (400 en cualquier otro caso).
  const handleBorrarCuenta = async () => {
    if (!cuentaABorrar) return;
    setBorrando(true);
    try {
      await borrarCuenta(ejercicioId, cuentaABorrar.id);
      toast.success(`Cuenta ${cuentaABorrar.codigo_full} borrada`);
      setCuentaABorrar(null);
      loadData();
      refrescarBusqueda();
      notifyDataChanged("ejercicios");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo borrar la cuenta");
    } finally {
      setBorrando(false);
    }
  };

  const goBack = async () => {
    try {
      const ejercicio = await getEjercicio(ejercicioId);
      navigate(`/empresas/${ejercicio.data.empresa_id}`);
    } catch {
      navigate("/empresas");
    }
  };

  const conflictos = pendientes.filter((c) => c.estado === "CONFLICTO");
  const solosPendientes = pendientes.filter((c) => c.estado === "PENDIENTE_ASIGNAR");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalPendientes = pendientes.length + errores.length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="page-title">
              Revisión de Cuentas
            </h1>
            <p className="text-muted-foreground mt-1">
              Asigna manualmente las cuentas no clasificadas automáticamente
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={abrirAlta} data-testid="anadir-cuenta-btn">
            <Plus className="h-4 w-4 mr-2" />
            Añadir cuenta
          </Button>
          {totalPendientes === 0 && (
            <Button onClick={() => navigate(`/ejercicios/${ejercicioId}/dashboard`)}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Todo Listo - Ver Dashboard
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <span className="text-sm text-muted-foreground">Pendientes</span>
            </div>
            <p className="text-3xl font-mono font-bold mt-2">{solosPendientes.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <span className="text-sm text-muted-foreground">Conflictos</span>
            </div>
            <p className="text-3xl font-mono font-bold mt-2">{conflictos.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-rose-600" />
              <span className="text-sm text-muted-foreground">Errores</span>
            </div>
            <p className="text-3xl font-mono font-bold mt-2">{errores.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="pendientes" data-testid="tab-pendientes">
            Pendientes ({solosPendientes.length})
          </TabsTrigger>
          <TabsTrigger value="conflictos" data-testid="tab-conflictos">
            Conflictos ({conflictos.length})
          </TabsTrigger>
          <TabsTrigger value="errores" data-testid="tab-errores">
            Errores ({errores.length})
          </TabsTrigger>
          <TabsTrigger value="buscar" className="gap-1.5" data-testid="tab-buscar">
            <Search className="h-3.5 w-3.5" />
            Buscar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendientes" className="space-y-3">
          {seleccionadas.size > 0 && (
            <div className="flex items-center justify-between rounded-md border bg-primary/5 px-4 py-2">
              <span className="text-sm font-medium">{seleccionadas.size} cuenta{seleccionadas.size > 1 ? "s" : ""} seleccionada{seleccionadas.size > 1 ? "s" : ""}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSeleccionadas(new Set())}>
                  Quitar selección
                </Button>
                <Button size="sm" onClick={() => { setBulkTipo("BALANCE"); setBulkPartida(""); setBulkGuardar(false); setBulkOpen(true); }} data-testid="asignar-masivo-btn">
                  Asignar {seleccionadas.size} a la vez
                </Button>
              </div>
            </div>
          )}
          <CuentasTable
            cuentas={solosPendientes}
            onSelect={handleSelectCuenta}
            onDelete={setCuentaABorrar}
            emptyMessage="No hay cuentas pendientes de asignar"
            selectable
            seleccionadas={seleccionadas}
            onToggle={toggleSel}
            onToggleAll={toggleTodas}
          />
        </TabsContent>

        <TabsContent value="conflictos">
          <CuentasTable
            cuentas={conflictos}
            onSelect={handleSelectCuenta}
            onDelete={setCuentaABorrar}
            emptyMessage="No hay conflictos"
          />
        </TabsContent>

        <TabsContent value="errores">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                  <TableHead>Motivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {errores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No hay errores de formato
                    </TableCell>
                  </TableRow>
                ) : (
                  errores.map((cuenta) => (
                    <TableRow key={cuenta.id}>
                      <TableCell className="font-mono">{cuenta.codigo_full}</TableCell>
                      <TableCell>{cuenta.nombre}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(cuenta.saldo)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="destructive">{cuenta.motivo}</Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="buscar">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Buscar y reclasificar cuentas</CardTitle>
              <p className="text-sm text-muted-foreground">
                Busca cualquier cuenta del ejercicio (clasificada o no) por código o nombre y reclasifícala.
              </p>
              <div className="relative max-w-md mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Código (prefijo) o nombre de la cuenta..."
                  className="pl-9"
                  data-testid="buscar-cuentas-input"
                />
              </div>
            </CardHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                  <TableHead>Clasificación actual</TableHead>
                  <TableHead className="w-[160px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!busqueda.trim() ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Escribe un código o nombre para buscar cuentas
                    </TableCell>
                  </TableRow>
                ) : buscando ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Buscando...
                    </TableCell>
                  </TableRow>
                ) : resultadosBusqueda.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Sin resultados para "{busqueda.trim()}"
                    </TableCell>
                  </TableRow>
                ) : (
                  resultadosBusqueda.map((cuenta) => (
                    <TableRow key={cuenta.id} className="hover:bg-muted/50">
                      <TableCell className="font-mono">{cuenta.codigo_full}</TableCell>
                      <TableCell className="max-w-[240px] truncate">{cuenta.nombre}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        <span className={cuenta.saldo >= 0 ? "text-emerald-600" : "text-rose-600"}>
                          {formatCurrency(cuenta.saldo)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {cuenta.clasificacion_actual ? (
                          <Badge variant="outline" className="text-xs max-w-[260px] truncate">
                            {cuenta.clasificacion_actual.tipo_destino} · {cuenta.clasificacion_actual.partida_pgc}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            Sin clasificar
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSelectCuenta(cuenta)}
                            data-testid={`reclasificar-cuenta-${cuenta.id}`}
                          >
                            Reclasificar
                          </Button>
                          {!esCuentaDeAsiento(cuenta.codigo_full) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => {
                                setCuentaARenombrar(cuenta);
                                setNuevoNombre(cuenta.nombre || "");
                              }}
                              aria-label={`Renombrar cuenta ${cuenta.codigo_full}`}
                              title="Renombrar la cuenta (en toda la empresa)"
                              data-testid={`renombrar-cuenta-${cuenta.id}`}
                            >
                              <Pencil className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          )}
                          {cuenta.motivo === "Alta manual" && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => setCuentaABorrar(cuenta)}
                              aria-label={`Borrar cuenta ${cuenta.codigo_full}`}
                              data-testid={`borrar-cuenta-${cuenta.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-rose-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assignment Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Asignar Cuenta</SheetTitle>
            <SheetDescription>
              Asigna esta cuenta a una partida del Balance o PyG
            </SheetDescription>
          </SheetHeader>

          {selectedCuenta && (
            <div className="space-y-6 mt-6">
              {/* Cuenta Info */}
              <div className="p-4 rounded-lg bg-muted">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Código</p>
                    <p className="font-mono font-semibold">{selectedCuenta.codigo_full}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Cuenta 3 dígitos</p>
                    <p className="font-mono font-semibold">{selectedCuenta.cuenta3}</p>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-muted-foreground text-sm">Nombre</p>
                  <p className="font-medium">{selectedCuenta.nombre}</p>
                </div>
                <div className="mt-2">
                  <p className="text-muted-foreground text-sm">Saldo</p>
                  <p className={`font-mono font-semibold ${selectedCuenta.saldo >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {formatCurrency(selectedCuenta.saldo)}
                  </p>
                </div>
              </div>

              {/* Tipo Destino */}
              <div className="space-y-2">
                <Label>Destino</Label>
                <Select value={tipoDestino} onValueChange={(v) => { setTipoDestino(v); setPartidaSeleccionada(""); }}>
                  <SelectTrigger data-testid="tipo-destino-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BALANCE">Balance de Situación</SelectItem>
                    <SelectItem value="PYG">Pérdidas y Ganancias</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Partida */}
              <div className="space-y-2">
                <Label>Partida PGC</Label>
                <Select value={partidaSeleccionada} onValueChange={setPartidaSeleccionada}>
                  <SelectTrigger data-testid="partida-select">
                    <SelectValue placeholder="Selecciona una partida" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {(tipoDestino === "BALANCE" ? partidasBalance : partidasPyg).map((partida) => (
                      <SelectItem key={partida} value={partida}>
                        {partida}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Guardar reglas */}
              <div className="space-y-3">
                <Label className="text-muted-foreground">Guardar regla para el futuro</Label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="guardar-empresa"
                    checked={guardarEmpresa}
                    onCheckedChange={setGuardarEmpresa}
                    data-testid="guardar-empresa-check"
                  />
                  <label htmlFor="guardar-empresa" className="text-sm">
                    Guardar regla para esta empresa
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="guardar-global"
                    checked={guardarGlobal}
                    onCheckedChange={setGuardarGlobal}
                    data-testid="guardar-global-check"
                  />
                  <label htmlFor="guardar-global" className="text-sm">
                    Guardar regla global (todas las empresas)
                  </label>
                </div>
              </div>

              {(guardarEmpresa || guardarGlobal) && (
                <p className="text-[11px] text-muted-foreground -mt-1">
                  Al guardar la regla se clasificarán también las demás cuentas <span className="font-mono">{selectedCuenta.cuenta3}</span> aún pendientes.
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setSheetOpen(false)}>
                  Cancelar
                </Button>
                <Button className="flex-1" onClick={handleAsignar} disabled={saving} data-testid="asignar-btn">
                  {saving ? "Guardando..." : "Asignar"}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Sheet de asignación MASIVA */}
      <Sheet open={bulkOpen} onOpenChange={setBulkOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Asignar {seleccionadas.size} cuentas</SheetTitle>
            <SheetDescription>
              Se asignará la misma partida a todas las cuentas seleccionadas.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-6 mt-6">
            <div className="space-y-2">
              <Label>Destino</Label>
              <Select value={bulkTipo} onValueChange={(v) => { setBulkTipo(v); setBulkPartida(""); }}>
                <SelectTrigger data-testid="bulk-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="BALANCE">Balance de Situación</SelectItem>
                  <SelectItem value="PYG">Pérdidas y Ganancias</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Partida PGC</Label>
              <Select value={bulkPartida} onValueChange={setBulkPartida}>
                <SelectTrigger data-testid="bulk-partida-select">
                  <SelectValue placeholder="Selecciona una partida" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {(bulkTipo === "BALANCE" ? partidasBalance : partidasPyg).map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="bulk-guardar" checked={bulkGuardar} onCheckedChange={setBulkGuardar} data-testid="bulk-guardar-check" />
              <label htmlFor="bulk-guardar" className="text-sm">
                Guardar regla para esta empresa (y clasificar el resto de pendientes iguales)
              </label>
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="flex-1" onClick={() => setBulkOpen(false)}>Cancelar</Button>
              <Button className="flex-1" onClick={handleAsignarMasivo} disabled={saving} data-testid="bulk-asignar-btn">
                {saving ? "Guardando..." : `Asignar ${seleccionadas.size}`}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ¿Aplicar la regla al resto de empresas? */}
      <AlertDialog open={!!promptGlobal} onOpenChange={(o) => !o && setPromptGlobal(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Aplicar también al resto de tus empresas?</AlertDialogTitle>
            <AlertDialogDescription>
              Has guardado la regla para esta empresa. ¿Quieres aplicar la misma clasificación
              {promptGlobal?.cuenta3s?.length ? ` de ${promptGlobal.cuenta3s.map((c) => c).join(", ")}` : ""} a las cuentas aún pendientes de <b>todas tus empresas</b>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPromptGlobal(null)}>Solo esta empresa</AlertDialogCancel>
            <AlertDialogAction onClick={handleAplicarGlobal} data-testid="aplicar-global-btn">
              Sí, aplicar a todas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* F1: Alta manual de cuenta */}
      <Dialog open={altaOpen} onOpenChange={setAltaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir cuenta</DialogTitle>
            <DialogDescription>
              Da de alta una cuenta nueva en este ejercicio.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCrearCuenta} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="alta-codigo">Código de cuenta</Label>
              <Input
                id="alta-codigo"
                value={altaCodigo}
                onChange={(e) => setAltaCodigo(e.target.value)}
                placeholder="Ej. 4300001"
                className="font-mono"
                autoFocus
                data-testid="alta-codigo-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="alta-nombre">Nombre</Label>
              <Input
                id="alta-nombre"
                value={altaNombre}
                onChange={(e) => setAltaNombre(e.target.value)}
                placeholder="Nombre de la cuenta"
                data-testid="alta-nombre-input"
              />
            </div>

            {/* Clasificación manual (opcional) */}
            <div className="space-y-3 rounded-md border p-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="alta-clasificar"
                  checked={altaClasificar}
                  onCheckedChange={(v) => { setAltaClasificar(!!v); setAltaPartida(""); }}
                  data-testid="alta-clasificar-check"
                />
                <label htmlFor="alta-clasificar" className="text-sm">
                  Clasificar manualmente (opcional)
                </label>
              </div>
              {altaClasificar ? (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Destino</Label>
                    <Select value={altaTipo} onValueChange={(v) => { setAltaTipo(v); setAltaPartida(""); }}>
                      <SelectTrigger data-testid="alta-tipo-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BALANCE">Balance de Situación</SelectItem>
                        <SelectItem value="PYG">Pérdidas y Ganancias</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Partida PGC</Label>
                    <Select value={altaPartida} onValueChange={setAltaPartida}>
                      <SelectTrigger data-testid="alta-partida-select">
                        <SelectValue placeholder="Selecciona una partida" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {(altaTipo === "BALANCE" ? partidasBalance : partidasPyg).map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Si no la clasificas, se creará como pendiente y podrás asignarla después (o el sistema la clasificará automáticamente).
                </p>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Se crea con saldo 0. Para movimientos con importe usa un asiento manual.
            </p>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAltaOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={creando} data-testid="alta-crear-btn">
                {creando ? "Creando..." : "Crear cuenta"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Renombrar cuenta (sept-2026): en importadas se propaga a todos los
          ejercicios de la empresa y se re-aplica al reimportar */}
      <Dialog open={!!cuentaARenombrar} onOpenChange={(o) => !o && setCuentaARenombrar(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renombrar la cuenta {cuentaARenombrar?.codigo_full}</DialogTitle>
            <DialogDescription>
              {cuentaARenombrar?.motivo === "Alta manual"
                ? "Cuenta creada a mano: el nombre cambia solo en este ejercicio."
                : "El nombre nuevo se aplica en TODOS los ejercicios de la empresa y se mantiene aunque vuelvas a importar."}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleRenombrar();
            }}
          >
            <div className="space-y-2 py-2">
              <Label htmlFor="renombrar-nombre">Nombre nuevo</Label>
              <Input
                id="renombrar-nombre"
                value={nuevoNombre}
                onChange={(e) => setNuevoNombre(e.target.value)}
                placeholder="Ej.: Prestación de Servicios (I+D)"
                autoFocus
                data-testid="renombrar-nombre-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCuentaARenombrar(null)}
                disabled={renombrando}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={renombrando || !nuevoNombre.trim()}
                data-testid="confirmar-renombrar-cuenta"
              >
                {renombrando ? "Guardando..." : "Renombrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* F1: confirmar borrado de cuenta creada a mano */}
      <AlertDialog open={!!cuentaABorrar} onOpenChange={(o) => !o && setCuentaABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar la cuenta {cuentaABorrar?.codigo_full}?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la cuenta
              {cuentaABorrar?.nombre ? ` "${cuentaABorrar.nombre}"` : ""} creada manualmente.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={borrando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleBorrarCuenta();
              }}
              disabled={borrando}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirmar-borrar-cuenta"
            >
              {borrando ? "Borrando..." : "Borrar cuenta"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CuentasTable({ cuentas, onSelect, onDelete, emptyMessage, selectable = false, seleccionadas, onToggle, onToggleAll }) {
  const ids = cuentas.map((c) => c.id);
  const todasMarcadas = selectable && ids.length > 0 && ids.every((id) => seleccionadas.has(id));
  const colSpan = selectable ? 6 : 5;
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={todasMarcadas}
                  onCheckedChange={(v) => onToggleAll(ids, !!v)}
                  aria-label="Seleccionar todas"
                  data-testid="seleccionar-todas"
                />
              </TableHead>
            )}
            <TableHead>Código</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead className="text-right">Saldo</TableHead>
            {selectable && <TableHead>Sugerencia</TableHead>}
            <TableHead>Motivo</TableHead>
            <TableHead className="w-[140px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cuentas.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="text-center py-8 text-muted-foreground">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            cuentas.map((cuenta) => {
              const sug = cuenta.sugerencia && SUG[cuenta.sugerencia.bucket];
              const marcada = selectable && seleccionadas.has(cuenta.id);
              return (
                <TableRow key={cuenta.id} className={marcada ? "bg-primary/5" : "hover:bg-muted/50"}>
                  {selectable && (
                    <TableCell>
                      <Checkbox
                        checked={marcada}
                        onCheckedChange={() => onToggle(cuenta.id)}
                        aria-label={`Seleccionar ${cuenta.codigo_full}`}
                        data-testid={`sel-${cuenta.id}`}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-mono">{cuenta.codigo_full}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{cuenta.nombre}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    <span className={cuenta.saldo >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                      {formatCurrency(cuenta.saldo)}
                    </span>
                  </TableCell>
                  {selectable && (
                    <TableCell>
                      {sug ? (
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${sug.cls}`}>{sug.label}</span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{cuenta.motivo}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onSelect(cuenta)}
                        data-testid={`asignar-cuenta-${cuenta.id}`}
                      >
                        Asignar
                      </Button>
                      {onDelete && cuenta.motivo === "Alta manual" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => onDelete(cuenta)}
                          aria-label={`Borrar cuenta ${cuenta.codigo_full}`}
                          data-testid={`borrar-cuenta-${cuenta.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Card>
  );
}
